import { useState } from 'react';
import { Phone, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypes = [
  { value: 'residential', label: 'Residential Electrical' },
  { value: 'commercial', label: 'Commercial Services' },
  { value: 'emergency', label: 'Emergency Repair' },
  { value: 'panel-upgrade', label: 'Panel Upgrade' },
  { value: 'lighting', label: 'Lighting Installation' },
  { value: 'outlet-switch', label: 'Outlet & Switch Work' },
  { value: 'other', label: 'Other' },
];

export const QuoteForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    preferred_time: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formDataToSubmit = new FormData();
    Object.keys(formData).forEach(key => {
      formDataToSubmit.append(key, formData[key]);
    });
    // Add Web3Forms Access Key (Placeholder)
    formDataToSubmit.append("access_key", "YOUR_ACCESS_KEY_HERE");
    formDataToSubmit.append("from_name", "Fredjoules Web Inquiry");
    formDataToSubmit.append("subject", `New Quote Request from ${formData.name}`);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataToSubmit
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        toast.success('Quote request submitted successfully!', {
          description: "We'll get back to you within 24 hours.",
        });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote', {
        description: error.message || 'Please try again or call us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  if (isSubmitted) {
    return (
      <section id="contact" className="py-24 bg-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-zinc-800 border border-white/10 p-12">
            <div className="w-20 h-20 bg-green-500/20 mx-auto mb-6 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3" data-testid="success-heading">
              Thank You!
            </h3>
            <p className="text-zinc-400 mb-6" data-testid="success-message">
              Your quote request has been submitted. Our team will contact you within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+19712652635"
                className="btn-primary flex items-center justify-center gap-2 px-6 py-3 font-semibold"
                data-testid="success-call-button"
              >
                <Phone className="w-4 h-4" />
                Call Now: (971) 265-2635
              </a>
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    service_type: '',
                    preferred_time: '',
                    message: '',
                  });
                }}
                className="btn-secondary px-6 py-3 font-medium"
                data-testid="submit-another-button"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-24 bg-zinc-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Side - Info */}
          <div>
            <span className="overline" data-testid="contact-overline">Get In Touch</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 tracking-tight mb-6" data-testid="contact-heading">
              Request a Free Quote
            </h2>
            <p className="text-zinc-300 text-lg mb-8">
              Fill out the form and we'll get back to you within 24 hours. For immediate assistance, give us a call.
            </p>

            <div className="space-y-6">
              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-400 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-black" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Call Us</h4>
                  <a
                    href="tel:+19712652635"
                    className="text-2xl font-bold text-yellow-400 phone-link"
                    data-testid="contact-phone"
                  >
                    (971) 265-2635
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="p-6 bg-zinc-800 border border-white/10">
                <h4 className="font-semibold text-white mb-4">Business Hours</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Monday - Sunday</span>
                    <span className="text-white font-medium">8:00 AM - 8:00 PM</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <span className="text-xs text-green-500 font-medium uppercase tracking-wider">
                    ● Open 7 Days a Week
                  </span>
                </div>
              </div>

              {/* Address */}
              <div className="text-zinc-400">
                <h4 className="font-semibold text-white mb-2">Location</h4>
                <p data-testid="contact-address">
                  8644 SW Canyon Rd #1590<br />
                  Portland, OR 97225
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-zinc-800 border border-white/10 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input bg-zinc-900 border-white/10 text-white"
                    data-testid="form-name"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input bg-zinc-900 border-white/10 text-white"
                    data-testid="form-phone"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input bg-zinc-900 border-white/10 text-white"
                  data-testid="form-email"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Service Type */}
                <div className="space-y-2">
                  <Label htmlFor="service_type" className="text-white">Service Type *</Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => handleSelectChange('service_type', value)}
                    required
                  >
                    <SelectTrigger
                      id="service_type"
                      className="form-input bg-zinc-900 border-white/10 text-white"
                      data-testid="form-service-type"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      {serviceTypes.map((service) => (
                        <SelectItem
                          key={service.value}
                          value={service.value}
                          className="text-white hover:bg-zinc-700 focus:bg-zinc-700"
                        >
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred Time */}
                <div className="space-y-2">
                  <Label htmlFor="preferred_time" className="text-white">Preferred Time</Label>
                  <Select
                    value={formData.preferred_time}
                    onValueChange={(value) => handleSelectChange('preferred_time', value)}
                  >
                    <SelectTrigger
                      id="preferred_time"
                      className="form-input bg-zinc-900 border-white/10 text-white"
                      data-testid="form-preferred-time"
                    >
                      <SelectValue placeholder="Select time slot" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      <SelectItem value="morning" className="text-white hover:bg-zinc-700 focus:bg-zinc-700">Morning (8AM - 12PM)</SelectItem>
                      <SelectItem value="afternoon" className="text-white hover:bg-zinc-700 focus:bg-zinc-700">Afternoon (12PM - 4PM)</SelectItem>
                      <SelectItem value="evening" className="text-white hover:bg-zinc-700 focus:bg-zinc-700">Evening (4PM - 8PM)</SelectItem>
                      <SelectItem value="flexible" className="text-white hover:bg-zinc-700 focus:bg-zinc-700">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-white">Project Details</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your electrical needs..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-input bg-zinc-900 border-white/10 text-white resize-none"
                  data-testid="form-message"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="form-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Request Free Quote
                  </>
                )}
              </button>

              <p className="text-xs text-zinc-500 text-center">
                By submitting this form, you agree to be contacted regarding your request.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
